// solution-dilution.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Pengenceran Larutan - Hitung Pengenceran Menggunakan Rumus C1V1=C2V2";
const DESCRIPTION =
  "Kalkulator pengenceran larutan gratis - hitung konsentrasi dan volume yang diperlukan untuk mengencerkan larutan kimia secara akurat. Gunakan rumus C1V1=C2V2 dan dapatkan hasil instan dengan faktor pengenceran dan volume pelarut.";
const KEYWORDS =
  "kalkulator pengenceran larutan, rumus pengenceran, C1V1=C2V2, hitung pengenceran, faktor pengenceran, pengenceran larutan kimia, kalkulator molaritas, dilution calculator, pengenceran konsentrat";
const OG_URL = "https://do-calculate.com/calculator/id/solution-dilution/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Pengenceran Larutan",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Pengenceran Larutan",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Apa rumus pengenceran larutan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Rumus pengenceran larutan adalah C1V1 = C2V2, di mana C1 adalah konsentrasi awal, V1 volume larutan awal, C2 konsentrasi akhir, dan V2 volume akhir. Rumus ini digunakan untuk menghitung nilai yang tidak diketahui ketika tiga nilai lainnya diketahui.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung volume pelarut yang diperlukan untuk pengenceran?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Volume pelarut yang diperlukan = volume akhir - volume larutan awal. Misalnya, jika Anda ingin menyiapkan 100 ml larutan encer dan membutuhkan 10 ml larutan pekat, maka volume pelarut = 100 - 10 = 90 ml.",
        },
      },
      {
        "@type": "Question",
        name: "Apa itu faktor pengenceran?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Faktor pengenceran adalah rasio antara konsentrasi awal dan konsentrasi akhir, atau rasio antara volume akhir dan volume awal. Faktor pengenceran 10 berarti konsentrasi berkurang menjadi sepersepuluh dari nilai aslinya.",
        },
      },
    ],
  },
];

const CONCENTRATION_UNITS = {
  M: { value: 1, name: "Molar (M)" },
  mM: { value: 0.001, name: "miliMolar (mM)" },
  "µM": { value: 0.000001, name: "mikroMolar (µM)" },
  "nM": { value: 0.000000001, name: "nanoMolar (nM)" },
  "%": { value: 1, name: "Persen (%)" },
  "g/L": { value: 1, name: "gram/Liter (g/L)" },
  "mg/mL": { value: 1, name: "miligram/mililiter (mg/mL)" },
  "µg/mL": { value: 1, name: "mikrogram/mililiter (µg/mL)" },
};

const VOLUME_UNITS = {
  L: { value: 1, name: "Liter (L)" },
  mL: { value: 0.001, name: "mililiter (mL)" },
  "µL": { value: 0.000001, name: "mikroliter (µL)" },
};

function DilutionCalculator() {
  const [calculationMode, setCalculationMode] = useState("calculate_V1");
  const [C1, setC1] = useState("");
  const [V1, setV1] = useState("");
  const [C2, setC2] = useState("");
  const [V2, setV2] = useState("");
  const [C1Unit, setC1Unit] = useState("M");
  const [V1Unit, setV1Unit] = useState("mL");
  const [C2Unit, setC2Unit] = useState("mM");
  const [V2Unit, setV2Unit] = useState("mL");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (shouldCalculate()) {
      calculateResult();
    }
  }, [C1, V1, C2, V2, C1Unit, V1Unit, C2Unit, V2Unit, calculationMode]);

  const shouldCalculate = () => {
    const inputs = [C1, V1, C2, V2];
    const filledInputs = inputs.filter(
      (val) => val !== "" && val !== null && !isNaN(parseFloat(val))
    );
    if (calculationMode === "dilution_factor") {
      return filledInputs.length >= 2;
    }
    return filledInputs.length >= 3;
  };

  const calculateResult = () => {
    setError("");
    try {
      const c1Val = parseFloat(C1);
      const v1Val = parseFloat(V1);
      const c2Val = parseFloat(C2);
      const v2Val = parseFloat(V2);

      if (calculationMode !== "dilution_factor") {
        const values = [c1Val, v1Val, c2Val, v2Val].filter((v) => !isNaN(v));
        if (values.some((v) => v <= 0)) {
          setError("Semua nilai harus lebih besar dari nol");
          setResult(null);
          return;
        }
      }

      let calculatedValue, dilutionFactor, solventVolume, calculationType;

      switch (calculationMode) {
        case "calculate_V1":
          if (isNaN(c1Val) || isNaN(c2Val) || isNaN(v2Val)) {
            setError("Harap masukkan nilai C1, C2, dan V2");
            setResult(null);
            return;
          }
          if (c2Val > c1Val) {
            setError("Konsentrasi akhir tidak boleh lebih besar dari konsentrasi awal");
            setResult(null);
            return;
          }
          const c1InBaseUnit = c1Val * (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          const c2InBaseUnit = c2Val * (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          const v2InLiters = v2Val * VOLUME_UNITS[V2Unit].value;
          calculatedValue = (c2InBaseUnit * v2InLiters) / c1InBaseUnit;
          calculatedValue = calculatedValue / VOLUME_UNITS[V1Unit].value;
          dilutionFactor = c1Val / c2Val;
          solventVolume = v2Val - calculatedValue;
          calculationType = "Volume larutan awal (V1)";
          setResult({
            value: calculatedValue, unit: V1Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: c1Val, C1Unit, V1: calculatedValue, V1Unit, C2: c2Val, C2Unit, V2: v2Val, V2Unit
          });
          break;

        case "calculate_C1":
          if (isNaN(v1Val) || isNaN(c2Val) || isNaN(v2Val)) {
            setError("Harap masukkan nilai V1, C2, dan V2");
            setResult(null);
            return;
          }
          const c2Base = c2Val * (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          const v1InLiters = v1Val * VOLUME_UNITS[V1Unit].value;
          const v2InL = v2Val * VOLUME_UNITS[V2Unit].value;
          calculatedValue = (c2Base * v2InL) / v1InLiters;
          calculatedValue = calculatedValue / (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          dilutionFactor = calculatedValue / c2Val;
          solventVolume = v2Val - v1Val;
          calculationType = "Konsentrasi awal (C1)";
          setResult({
            value: calculatedValue, unit: C1Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: calculatedValue, C1Unit, V1: v1Val, V1Unit, C2: c2Val, C2Unit, V2: v2Val, V2Unit
          });
          break;

        case "calculate_V2":
          if (isNaN(c1Val) || isNaN(v1Val) || isNaN(c2Val)) {
            setError("Harap masukkan nilai C1, V1, dan C2");
            setResult(null);
            return;
          }
          if (c2Val > c1Val) {
            setError("Konsentrasi akhir tidak boleh lebih besar dari konsentrasi awal");
            setResult(null);
            return;
          }
          const c1Base2 = c1Val * (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          const c2Base2 = c2Val * (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          const v1InL2 = v1Val * VOLUME_UNITS[V1Unit].value;
          calculatedValue = (c1Base2 * v1InL2) / c2Base2;
          calculatedValue = calculatedValue / VOLUME_UNITS[V2Unit].value;
          dilutionFactor = c1Val / c2Val;
          solventVolume = calculatedValue - v1Val;
          calculationType = "Volume akhir (V2)";
          setResult({
            value: calculatedValue, unit: V2Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: c1Val, C1Unit, V1: v1Val, V1Unit, C2: c2Val, C2Unit, V2: calculatedValue, V2Unit
          });
          break;

        case "calculate_C2":
          if (isNaN(c1Val) || isNaN(v1Val) || isNaN(v2Val)) {
            setError("Harap masukkan nilai C1, V1, dan V2");
            setResult(null);
            return;
          }
          if (v1Val > v2Val) {
            setError("Volume akhir harus lebih besar dari atau sama dengan volume awal");
            setResult(null);
            return;
          }
          const c1Base3 = c1Val * (C1Unit === "%" ? 1 : CONCENTRATION_UNITS[C1Unit].value);
          const v1InL3 = v1Val * VOLUME_UNITS[V1Unit].value;
          const v2InL3 = v2Val * VOLUME_UNITS[V2Unit].value;
          calculatedValue = (c1Base3 * v1InL3) / v2InL3;
          calculatedValue = calculatedValue / (C2Unit === "%" ? 1 : CONCENTRATION_UNITS[C2Unit].value);
          dilutionFactor = c1Val / calculatedValue;
          solventVolume = v2Val - v1Val;
          calculationType = "Konsentrasi akhir (C2)";
          setResult({
            value: calculatedValue, unit: C2Unit, type: calculationType,
            dilutionFactor, solventVolume, solventUnit: V2Unit,
            C1: c1Val, C1Unit, V1: v1Val, V1Unit, C2: calculatedValue, C2Unit, V2: v2Val, V2Unit
          });
          break;

        case "dilution_factor":
          let df;
          if (!isNaN(c1Val) && !isNaN(c2Val)) {
            df = c1Val / c2Val;
          } else if (!isNaN(v2Val) && !isNaN(v1Val)) {
            df = v2Val / v1Val;
          } else {
            setError("Harap masukkan (C1 dan C2) atau (V1 dan V2) untuk menghitung faktor pengenceran");
            setResult(null);
            return;
          }
          setResult({
            dilutionFactor: df, type: "Faktor Pengenceran",
            C1: c1Val || null, C1Unit, C2: c2Val || null, C2Unit,
            V1: v1Val || null, V1Unit, V2: v2Val || null, V2Unit
          });
          break;

        default:
          setError("Silakan pilih jenis perhitungan");
          setResult(null);
      }
    } catch (err) {
      setError("Terjadi kesalahan dalam perhitungan. Harap periksa nilai yang dimasukkan.");
      setResult(null);
    }
  };

  const handleClear = () => {
    setC1("");
    setV1("");
    setC2("");
    setV2("");
    setC1Unit("M");
    setV1Unit("mL");
    setC2Unit("mM");
    setV2Unit("mL");
    setCalculationMode("calculate_V1");
    setResult(null);
    setError("");
  };

  const renderInputFields = () => {
    return (
      <>
        <div className="row">
          <div className="col-sm-12">
            <label htmlFor="calculationMode" >Jenis Perhitungan</label>
            <select id="calculationMode" className="form-select" value={calculationMode} onChange={(e) => setCalculationMode(e.target.value)}>
              <option value="calculate_V1">Hitung volume larutan awal (V1)</option>
              <option value="calculate_C1">Hitung konsentrasi awal (C1)</option>
              <option value="calculate_V2">Hitung volume akhir (V2)</option>
              <option value="calculate_C2">Hitung konsentrasi akhir (C2)</option>
              <option value="dilution_factor">Hitung faktor pengenceran saja</option>
            </select>
          </div>
        </div>
        {calculationMode !== "calculate_C1" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C1" >Konsentrasi awal (C1){calculationMode === "dilution_factor" && " - Opsional"}</label>
              <input type="number" id="C1" className="form-control" value={C1} onChange={(e) => setC1(e.target.value)} min="0" step="any" placeholder="Masukkan konsentrasi awal" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C1Unit" >Satuan konsentrasi awal</label>
              <select id="C1Unit" className="form-select" value={C1Unit} onChange={(e) => setC1Unit(e.target.value)}>
                {Object.entries(CONCENTRATION_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {calculationMode !== "calculate_V1" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V1" >Volume larutan awal (V1){calculationMode === "dilution_factor" && " - Opsional"}</label>
              <input type="number" id="V1" className="form-control" value={V1} onChange={(e) => setV1(e.target.value)} min="0" step="any" placeholder="Masukkan volume awal" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V1Unit" >Satuan volume awal</label>
              <select id="V1Unit" className="form-select" value={V1Unit} onChange={(e) => setV1Unit(e.target.value)}>
                {Object.entries(VOLUME_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {calculationMode !== "calculate_C2" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C2" >Konsentrasi akhir (C2){calculationMode === "dilution_factor" && " - Opsional"}</label>
              <input type="number" id="C2" className="form-control" value={C2} onChange={(e) => setC2(e.target.value)} min="0" step="any" placeholder="Masukkan konsentrasi akhir" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="C2Unit" >Satuan konsentrasi akhir</label>
              <select id="C2Unit" className="form-select" value={C2Unit} onChange={(e) => setC2Unit(e.target.value)}>
                {Object.entries(CONCENTRATION_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {calculationMode !== "calculate_V2" && (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V2" >Volume akhir (V2){calculationMode === "dilution_factor" && " - Opsional"}</label>
              <input type="number" id="V2" className="form-control" value={V2} onChange={(e) => setV2(e.target.value)} min="0" step="any" placeholder="Masukkan volume akhir" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="V2Unit" >Satuan volume akhir</label>
              <select id="V2Unit" className="form-select" value={V2Unit} onChange={(e) => setV2Unit(e.target.value)}>
                {Object.entries(VOLUME_UNITS).map(([key, val]) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-sm-12">
            <button className="btn btn-secondary w-100" onClick={handleClear}>Hapus Semua</button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="ltr" >
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Pengenceran Larutan</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Pengenceran Larutan</strong> adalah alat ilmiah akurat yang membantu Anda <strong>menghitung konsentrasi dan volume</strong> yang diperlukan untuk mengencerkan larutan kimia menggunakan rumus C₁V₁ = C₂V₂. Dapatkan hasil instan dengan faktor pengenceran dan volume pelarut yang diperlukan. Ideal untuk laboratorium medis, penelitian ilmiah, dan industri kimia.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Hitung Pengenceran Larutan dengan Akurat</h2>
              </div>
              {renderInputFields()}
              {error && <div className="alert alert-danger mt-3" role="alert">{error}</div>}
            </div>

            {result && (
              <div className="result-section">
                <h3>Hasil</h3>
                <div className="table-responsive">
                  <table className="table table-bordered bmi-table">
                    <tbody>
                      {calculationMode === "dilution_factor" ? (
                        <tr>
                          <td><strong>Faktor Pengenceran</strong></td>
                          <td>{result.dilutionFactor.toFixed(2)}x</td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <td><strong>{result.type}</strong></td>
                            <td>{result.value.toFixed(4)} {result.unit}</td>
                          </tr>
                          <tr>
                            <td><strong>Faktor Pengenceran</strong></td>
                            <td>{result.dilutionFactor.toFixed(2)}x</td>
                          </tr>
                          <tr>
                            <td><strong>Volume Pelarut yang Diperlukan</strong></td>
                            <td>{result.solventVolume > 0 ? `${result.solventVolume.toFixed(4)} ${result.solventUnit}` : "Tidak diperlukan"}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="info-sections-container">
                  <div className="info-section">
                    <div className="info-section-header"><strong>Detail Perhitungan</strong></div>
                    <div className="info-section-body">
                      <p style={{ lineHeight: "1.8" }}><strong>Rumus yang Digunakan:</strong> C₁V₁ = C₂V₂</p>
                      {result.C1 !== null && <p style={{ lineHeight: "1.8" }}>Konsentrasi awal (C₁) = {result.C1} {result.C1Unit}</p>}
                      {result.V1 !== null && <p style={{ lineHeight: "1.8" }}>Volume awal (V₁) = {result.V1.toFixed(4)} {result.V1Unit}</p>}
                      {result.C2 !== null && <p style={{ lineHeight: "1.8" }}>Konsentrasi akhir (C₂) = {result.C2.toFixed(4)} {result.C2Unit}</p>}
                      {result.V2 !== null && <p style={{ lineHeight: "1.8" }}>Volume akhir (V₂) = {result.V2.toFixed(4)} {result.V2Unit}</p>}
                      {calculationMode !== "dilution_factor" && (
                        <div className="alert alert-info" style={{ marginTop: "15px", lineHeight: "1.8" }}>
                          <strong>Cara Penyiapan:</strong><br />
                          Ambil {result.V1.toFixed(4)} {result.V1Unit} dari larutan pekat, lalu tambahkan {result.solventVolume > 0 ? `${result.solventVolume.toFixed(4)} ${result.solventUnit}` : "tidak ada"} pelarut untuk mendapatkan {result.V2.toFixed(4)} {result.V2Unit} larutan encer.
                        </div>
                      )}
                    </div>
                  </div>
                  {result.dilutionFactor && (
                    <div className="info-section">
                      <div className="info-section-header"><strong>Memahami Faktor Pengenceran</strong></div>
                      <div className="info-section-body">
                        <p style={{ lineHeight: "1.8" }}>
                          Faktor pengenceran = {result.dilutionFactor.toFixed(2)}x berarti konsentrasi akhir menjadi {((1 / result.dilutionFactor) * 100).toFixed(2)}% dari konsentrasi awal.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>Apa itu Kalkulator Pengenceran Larutan?</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>Kalkulator pengenceran larutan adalah alat ilmiah akurat yang membantu Anda menghitung jumlah yang diperlukan untuk mengencerkan larutan kimia dengan cepat dan mudah. Kalkulator ini didasarkan pada rumus kimia terkenal C₁V₁ = C₂V₂, sebuah hukum dasar dalam kimia yang menyatakan prinsip kekekalan massa. Sederhananya, ketika Anda mengencerkan larutan, jumlah zat terlarut tetap konstan sementara volume dan konsentrasi berubah.</p>
                <p style={{ lineHeight: "1.8" }}>Menggunakan kalkulator ini sangat sederhana. Pertama, tentukan jenis perhitungan yang Anda inginkan: Apakah Anda perlu mengetahui volume larutan pekat? Konsentrasi akhir? Atau volume total yang diperlukan? Kedua, masukkan tiga nilai yang Anda ketahui. Kalkulator akan secara otomatis menghitung nilai keempat yang tidak diketahui. Ketiga, dapatkan hasil dengan detail lengkap termasuk faktor pengenceran dan volume pelarut yang perlu ditambahkan. Terakhir, ikuti petunjuk rinci untuk menyiapkan larutan secara akurat.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Penggunaan Kalkulator Pengenceran Larutan</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>Kalkulator pengenceran digunakan dalam berbagai bidang yang memerlukan akurasi tinggi dalam penyiapan larutan:</p>
                <p style={{ lineHeight: "1.8" }}><strong>Laboratorium Medis dan Farmasi:</strong> Apoteker dan teknisi medis perlu mengencerkan obat dan larutan intravena dengan presisi tinggi. Misalnya, mengencerkan larutan antibiotik dari konsentrasi 100 mg/mL menjadi 50 mg/mL untuk pasien tertentu. Kesalahan perhitungan dapat membahayakan nyawa pasien, jadi menggunakan kalkulator yang andal adalah suatu keharusan, bukan kemewahan.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Laboratorium Penelitian dan Universitas:</strong> Peneliti perlu menyiapkan larutan dengan konsentrasi yang sangat spesifik untuk eksperimen ilmiah. Baik itu larutan biologi atau kimia organik, akurasi dalam pengenceran secara langsung mempengaruhi hasil eksperimen dan reprodusibilitasnya. Larutan yang salah berarti waktu, uang, dan tenaga yang terbuang.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Industri Makanan dan Minuman:</strong> Pabrik makanan perlu mengencerkan konsentrat, perasa, dan pewarna dengan perbandingan yang tepat untuk mendapatkan produk dengan kualitas yang konsisten. Misalnya, mengencerkan konsentrat jus dari kadar gula 60% menjadi 12% untuk produk akhir. Perhitungan yang salah menghasilkan produk yang tidak dapat diterima secara komersial.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Aplikasi Lingkungan dan Pertanian:</strong> Insinyur pertanian menggunakan pengenceran untuk menyiapkan pestisida dan pupuk cair dengan konsentrasi yang aman dan efektif. Juga dalam pengolahan air dan pabrik pemurnian, di mana bahan kimia pengolahan seperti klorin diencerkan ke konsentrasi yang sesuai agar tidak membahayakan kesehatan masyarakat.</p>
              </div>
            </div>


            <div className="info-section">
              <div className="info-section-header"><strong>Memahami Rumus Pengenceran C₁V₁ = C₂V₂</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>Rumus pengenceran dasar C₁V₁ = C₂V₂ adalah salah satu rumus terpenting dalam kimia terapan. Mari kita pahami setiap simbol di dalamnya:</p>
                <div className="table-responsive" style={{ marginTop: "15px" }}>
                  <table className="table table-bordered">
                    <thead>
                      <tr><th>Simbol</th><th>Arti</th><th>Satuan Umum</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>C₁</td><td>Konsentrasi awal larutan pekat</td><td>M, mM, µM, %, g/L</td></tr>
                      <tr><td>V₁</td><td>Volume larutan pekat yang diperlukan</td><td>L, mL, µL</td></tr>
                      <tr><td>C₂</td><td>Konsentrasi akhir larutan encer</td><td>M, mM, µM, %, g/L</td></tr>
                      <tr><td>V₂</td><td>Volume total akhir larutan</td><td>L, mL, µL</td></tr>
                    </tbody>
                  </table>
                </div>
                <p style={{ lineHeight: "1.8" }}>Rumus ini menyatakan bahwa hasil kali konsentrasi dan volume sebelum pengenceran sama dengan hasil kalinya setelah pengenceran. Ini logis karena jumlah zat sebenarnya (jumlah molekul) tidak berubah, hanya didistribusikan ke volume yang lebih besar. Bayangkan Anda memiliki 10 kubus gula dalam segelas air, jika Anda menambahkan segelas air lagi, jumlah kubusnya tidak berubah tetapi rasanya menjadi kurang manis.</p>
                <p style={{ lineHeight: "1.8" }}>Faktor pengenceran adalah rasio antara kedua konsentrasi (C₁/C₂) atau antara kedua volume (V₂/V₁). Faktor pengenceran 10x berarti Anda membuat larutan sepuluh kali lebih lemah. Di laboratorium, pengenceran serial digunakan di mana pengenceran dilakukan secara bertahap, setiap tahap dengan faktor konstan seperti 1:10, untuk mendapatkan konsentrasi yang sangat kecil dengan akurasi tinggi.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Contoh Praktis Pengenceran Larutan</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>Mari kita lihat contoh nyata yang menunjukkan cara menggunakan Kalkulator Pengenceran:</p>
                <p style={{ lineHeight: "1.8" }}><strong>Contoh 1 - Laboratorium Medis:</strong> Anda memiliki larutan garam (NaCl) dengan konsentrasi 5 M, dan Anda membutuhkan 250 mL larutan 0,9%. Berapa mL yang Anda perlukan dari larutan pekat? Penyelesaian: Gunakan kalkulator dengan memasukkan C₁ = 5M, C₂ = 0,9%, V₂ = 250mL. Anda akan mendapatkan V₁ ≈ 3,9mL. Jadi ambil 3,9 mL larutan pekat dan tambahkan 246,1 mL air suling.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Contoh 2 - Laboratorium Penelitian:</strong> Anda ingin menyiapkan 500 mL larutan glukosa dengan konsentrasi 100 mM dari larutan stok dengan konsentrasi 1 M. Gunakan kalkulator: C₁ = 1M, C₂ = 100mM, V₂ = 500mL. Hasilnya: V₁ = 50mL. Cara penyiapan: Tuang 50 mL larutan glukosa pekat ke dalam labu ukur 500 mL, lalu tambahkan air suling hingga tanda batas.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Contoh 3 - Industri Makanan:</strong> Pabrik jus memiliki konsentrat jeruk dengan konsentrasi 65 briks, dan ingin memproduksi 1000 liter jus siap minum dengan konsentrasi 12 briks. Kalkulator memberikan: Ambil sekitar 185 liter konsentrat dan tambahkan 815 liter air olahan. Faktor pengenceran di sini sekitar 5,4x.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Contoh 4 - Pertanian:</strong> Pestisida dengan konsentrasi 40% harus diencerkan menjadi 2% untuk disemprotkan pada tanaman. Petani membutuhkan 20 liter larutan encer. Penyelesaian: V₁ = (2 × 20) / 40 = 1 liter. Jadi ambil 1 liter pestisida pekat dan campur dengan 19 liter air.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Tips Penggunaan yang Benar dan Kesalahan Umum</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>Untuk mendapatkan hasil terbaik dan menghindari kesalahan, ikuti panduan profesional ini:</p>
                <p style={{ lineHeight: "1.8" }}><strong>Pastikan satuan ukur:</strong> Kesalahan paling umum adalah mencampuradukkan satuan. Pastikan satuan konsentrasi kompatibel (misalnya semuanya dalam M atau semuanya dalam %), dan satuan volume juga kompatibel. Kalkulator menangani berbagai satuan, tetapi sebaiknya Anda sadar dengan apa yang Anda masukkan. Kesalahan kecil dalam mengkonversi mL ke L dapat berarti perbedaan antara larutan yang benar dan larutan yang 1000 kali lebih pekat!</p>
                <p style={{ lineHeight: "1.8" }}><strong>Tambahkan zat terlarut ke pelarut, bukan sebaliknya:</strong> Aturan emas di laboratorium: letakkan air terlebih dahulu dalam wadah, lalu tambahkan larutan pekat secara perlahan sambil diaduk. Ini sangat penting terutama dengan asam kuat dan basa. Menambahkan air ke asam pekat dapat menyebabkan reaksi termal yang hebat dan ledakan.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Gunakan alat ukur yang tepat:</strong> Akurasi pengukuran sangat penting. Untuk volume kecil (kurang dari 1 mL), gunakan mikropipet. Untuk volume sedang, gunakan pipet kaca atau gelas ukur. Untuk volume besar, gunakan labu ukur.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Perhatikan suhu dan penyimpanan:</strong> Beberapa larutan dipengaruhi oleh suhu, di mana konsentrasinya dapat berubah atau komponennya dapat terurai. Selalu baca instruksi penyimpanan bahan kimia. Larutan encer umumnya kurang stabil dibandingkan larutan pekat, jadi siapkan hanya jumlah yang Anda butuhkan.</p>
                <p style={{ lineHeight: "1.8" }}><strong>Dokumentasikan pekerjaan Anda:</strong> Selalu simpan catatan larutan yang Anda siapkan: tanggal, konsentrasi, volume, nama penyiap, tujuan penggunaan, dan nomor batch bahan awal. Ini tidak hanya untuk keselamatan, tetapi juga untuk melacak kesalahan jika terjadi dan untuk kepatuhan terhadap standar kualitas.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Pertanyaan Umum</strong></div>
              <div className="info-section-body">
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Apa rumus pengenceran larutan?</h3>
                  <p style={{ lineHeight: "1.8" }}>Rumus pengenceran larutan adalah C₁V₁ = C₂V₂, di mana C₁ adalah konsentrasi awal larutan pekat, V₁ volume larutan awal yang diperlukan, C₂ konsentrasi akhir yang diinginkan, dan V₂ volume total akhir. Rumus ini digunakan untuk menghitung nilai yang tidak diketahui ketika tiga nilai lainnya diketahui. Rumus ini didasarkan pada prinsip kekekalan massa, yaitu bahwa jumlah zat terlarut tetap konstan sebelum dan sesudah pengenceran.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Bagaimana cara menghitung volume pelarut yang diperlukan untuk pengenceran?</h3>
                  <p style={{ lineHeight: "1.8" }}>Volume pelarut yang diperlukan = volume akhir (V₂) - volume larutan awal (V₁). Misalnya, jika Anda ingin menyiapkan 100 mL larutan encer dan membutuhkan 10 mL larutan pekat, maka volume pelarut yang harus ditambahkan = 100 - 10 = 90 mL. Dalam praktiknya, lebih baik menempatkan pelarut terlebih dahulu dalam wadah, lalu menambahkan larutan pekat secara bertahap sambil diaduk, terutama ketika menangani bahan reaktif atau korosif.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Apa itu faktor pengenceran dan bagaimana cara menghitungnya?</h3>
                  <p style={{ lineHeight: "1.8" }}>Faktor pengenceran adalah rasio yang menyatakan seberapa besar larutan diencerkan, dan dihitung dengan membagi konsentrasi awal dengan konsentrasi akhir (C₁/C₂) atau dengan membagi volume akhir dengan volume awal (V₂/V₁). Faktor pengenceran 10 (ditulis 10x atau 1:10) berarti konsentrasi berkurang menjadi sepersepuluh dari nilai aslinya, yaitu larutan menjadi sepuluh kali lebih lemah. Dalam pengenceran serial, faktor pengenceran berturut-turut dikalikan untuk mendapatkan faktor pengenceran total.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Bisakah kalkulator digunakan dengan satuan yang berbeda?</h3>
                  <p style={{ lineHeight: "1.8" }}>Ya, kalkulator mendukung berbagai satuan untuk konsentrasi (M, mM, µM, nM, %, g/L, mg/mL, µg/mL) dan satuan volume (L, mL, µL). Yang penting adalah konsisten dalam memilih satuan yang sesuai untuk jenis larutan Anda. Misalnya, larutan biologis sering diukur dalam molaritas (M) atau mM, sedangkan larutan makanan dapat diukur dalam persen. Kalkulator secara otomatis mengkonversi antar satuan sesuai kebutuhan untuk mendapatkan hasil yang akurat.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Apa yang terjadi jika konsentrasi akhir lebih besar dari konsentrasi awal?</h3>
                  <p style={{ lineHeight: "1.8" }}>Dalam proses pengenceran normal, konsentrasi akhir harus selalu kurang dari atau sama dengan konsentrasi awal. Jika Anda memasukkan nilai konsentrasi akhir yang lebih besar dari konsentrasi awal, kalkulator akan menampilkan pesan kesalahan. Ini karena pengenceran berarti menambahkan pelarut untuk mengurangi konsentrasi, bukan sebaliknya. Jika Anda ingin meningkatkan konsentrasi, Anda perlu menambahkan zat terlarut itu sendiri atau menguapkan pelarut, dan ini adalah proses berbeda yang tidak dicakup oleh rumus pengenceran sederhana.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Bagaimana cara memastikan keakuratan hasil?</h3>
                  <p style={{ lineHeight: "1.8" }}>Untuk memastikan keakuratan hasil, ikuti langkah-langkah berikut: Pertama, periksa validitas nilai yang dimasukkan dan pastikan tidak ada kesalahan ketik. Kedua, tinjau satuan yang digunakan dan pastikan sesuai dengan sifat larutan. Ketiga, gunakan alat ukur yang terkalibrasi dan akurat di laboratorium. Keempat, ulangi proses perhitungan atau gunakan metode alternatif untuk verifikasi. Kelima, setelah menyiapkan larutan, Anda dapat mengukur konsentrasi aktual dengan metode analitik seperti titrasi atau spektrofotometri untuk memastikan kesesuaiannya dengan konsentrasi target.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Apa perbedaan antara pengenceran sederhana dan pengenceran serial?</h3>
                  <p style={{ lineHeight: "1.8" }}>Pengenceran sederhana adalah satu langkah di mana Anda mengencerkan larutan langsung dari konsentrasi awal ke konsentrasi akhir. Pengenceran serial adalah serangkaian pengenceran berturut-turut, di mana larutan encer dari setiap langkah digunakan sebagai sumber untuk langkah berikutnya. Pengenceran serial digunakan ketika diperlukan konsentrasi yang sangat rendah yang sulit disiapkan secara langsung dengan akurasi, seperti 10⁻⁶ M. Di laboratorium mikrobiologi, pengenceran serial sangat penting untuk menghitung bakteri dan menentukan konsentrasinya dalam sampel.</p>
                </div>
                <div style={{ marginTop: "15px" }}>
                  <h3 style={{ fontSize: "16px" }}>Apakah sifat larutan berubah setelah pengenceran?</h3>
                  <p style={{ lineHeight: "1.8" }}>Ya dan tidak. Konsentrasi pasti berubah, dan itulah tujuan pengenceran. Tetapi sifat kimia zat itu sendiri tidak berubah. Yang berubah adalah jumlah molekul per satuan volume. Beberapa sifat fisik mungkin terpengaruh seperti densitas, viskositas, dan titik didih, tetapi perubahannya biasanya kecil dalam pengenceran biasa. Dalam beberapa kasus, sifat kimia seperti pH mungkin terpengaruh, terutama dalam larutan penyangga. Juga, beberapa zat dapat mengendap atau kehilangan efektivitasnya jika diencerkan terlalu banyak.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DilutionCalculator;